import Image from 'next/image';
import type { ComponentType, ReactNode, SVGProps } from 'react';

import styles from '@/components/agents/agents.module.css';

import clsxm from '@/lib/clsxm';

import {
  CheckGlyph,
  delay,
  SectionHeading,
  tone,
} from '@/components/agents/primitives';
import { useReveal } from '@/components/agents/useReveal';

import { BLOOM_BANNER, BRAND, useEmployeesT } from './kit';

import AnthropicSvg from '~/svg/Anthropic.svg';
import DeepseekSvg from '~/svg/deepseek.svg';
import GoogleSvg from '~/svg/google.svg';
import MetaSvg from '~/svg/meta.svg';
import OpenAISvg from '~/svg/OpenAI.svg';
import XaiSvg from '~/svg/xai.svg';
import ZaiSvg from '~/svg/zai.svg';

type Logo = ComponentType<SVGProps<SVGSVGElement>>;

/** A model an employee can run on, picked from the catalogue at build time. */
export type ToolkitModel = { provider: string; title: string };

const PROVIDER_LOGOS: Record<string, { logo: Logo; className?: string }> = {
  OpenAI: { logo: OpenAISvg, className: 'fill-[#10101c]' },
  Anthropic: { logo: AnthropicSvg },
  Google: { logo: GoogleSvg },
  xAI: { logo: XaiSvg, className: 'text-[#10101c]' },
  Deepseek: { logo: DeepseekSvg },
  Meta: { logo: MetaSvg },
  'Z.ai': { logo: ZaiSvg },
};

/** Memory, knowledge, a computer, media, models and skills, as a bento. */
export default function EmployeesToolkit({
  models,
  modelCount,
}: {
  models: ToolkitModel[];
  modelCount: number;
}) {
  const { t } = useEmployeesT();
  const { ref, state } = useReveal<HTMLElement>(0.15);

  const cards: { title: string; body: string; visual: ReactNode }[] = [
    {
      title: t('They remember'),
      body: t(
        'Each employee keeps notes about you, your business and its own work, and learns from every conversation. You can read and edit all of it.',
      ),
      visual: <MemoryVisual />,
    },
    {
      title: t('They know your business'),
      body: t(
        'Add your website, documents and answers to common questions. They look things up there before they reply.',
      ),
      visual: <KnowledgeVisual />,
    },
    {
      title: t('A computer of their own'),
      body: t(
        'A private workspace to read files, run code and work through spreadsheets, plus web search and a browser.',
      ),
      visual: <ComputerVisual />,
    },
    {
      title: t('Images and video'),
      body: t(
        'Your designer makes banners and product shots. Your video producer writes the script and makes the clip.',
      ),
      visual: <MediaVisual />,
    },
    {
      title: t('The right model for each job'),
      body: t(
        'Choose the AI model behind every employee, from fast and affordable to the most capable, and how hard it thinks.',
      ),
      visual: <ModelsVisual models={models} modelCount={modelCount} />,
    },
    {
      title: t('Skills for the job'),
      body: t(
        'Built-in skills for research reports, data analysis and documents, plus skills you write yourself.',
      ),
      visual: <SkillsVisual />,
    },
  ];

  return (
    <section
      id='features'
      ref={ref}
      data-reveal={state}
      className={clsxm(styles.reveal, 'scroll-mt-8 py-20 sm:py-28')}
    >
      <div className='mx-auto max-w-7xl px-6 lg:px-8'>
        <SectionHeading
          title={t('Everything a good hire needs.')}
          tail={t('On day one.')}
          lead={t(
            'Every employee comes with a memory, your knowledge, a computer of its own and tools to make images and video.',
          )}
        />
        <ul className='mt-14 grid gap-4 sm:grid-cols-2 lg:grid-cols-3'>
          {cards.map((card, index) => (
            <li
              key={card.title}
              className={clsxm(
                styles.rv,
                'flex flex-col rounded-[28px] p-2',
                tone.surface,
                tone.line,
              )}
              style={delay((index % 3) * 100)}
            >
              <div
                aria-hidden='true'
                className='flex h-56 items-center justify-center overflow-hidden rounded-[22px] bg-[#f3f3f8] p-5 dark:bg-white/[0.04]'
              >
                {card.visual}
              </div>
              <div className='px-4 pb-5 pt-5'>
                <h3
                  className={clsxm(
                    'text-lg font-semibold tracking-[-0.015em]',
                    tone.ink,
                  )}
                >
                  {card.title}
                </h3>
                <p className={clsxm('mt-2 text-[15px] leading-6', tone.body)}>
                  {card.body}
                </p>
              </div>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

const panel =
  'rounded-2xl bg-white shadow-[0_14px_30px_-20px_rgba(22,12,90,0.45)] ring-1 ring-[#0f0f28]/[0.06] dark:bg-[#1b1a27] dark:ring-white/10';

function MemoryVisual() {
  const { t } = useEmployeesT();
  const notes = [
    {
      label: t('About {{brand}}', { brand: BRAND }),
      text: t('Small-batch perfumes, sold online and in boutiques'),
    },
    { label: t('Your preferences'), text: t('Short emails. No emoji.') },
    {
      label: t('Learned today'),
      text: t('Ana at Luma orders 24 bottles each season'),
      fresh: true,
    },
  ];
  return (
    <ul className='w-full max-w-[290px] space-y-2'>
      {notes.map((note) => (
        <li key={note.label} className={clsxm(panel, 'px-3.5 py-2')}>
          <p
            className={clsxm(
              'flex items-center gap-1.5 text-[11px] font-semibold',
              note.fresh
                ? 'text-primary-700 dark:text-primary-300'
                : tone.quiet,
            )}
          >
            {note.fresh ? (
              <span className='h-1.5 w-1.5 rounded-full bg-primary-500' />
            ) : null}
            {note.label}
          </p>
          <p
            className={clsxm(
              'mt-0.5 truncate text-[13px] font-medium',
              tone.ink,
            )}
          >
            {note.text}
          </p>
        </li>
      ))}
    </ul>
  );
}

function KnowledgeVisual() {
  const { t } = useEmployeesT();
  const sources = [
    {
      icon: '🌐',
      name: 'aurela.com',
      meta: t('{{count}} pages', { count: 42 }),
    },
    { icon: '📄', name: t('Wholesale price list.pdf'), meta: t('Document') },
    { icon: '💬', name: t('Shipping and returns'), meta: t('Q&A') },
  ];
  return (
    <div className={clsxm(panel, 'w-full max-w-[290px] p-2')}>
      <ul className='space-y-0.5'>
        {sources.map((source) => (
          <li
            key={source.name}
            className='flex items-center gap-3 rounded-xl px-2 py-1.5'
          >
            <span className='flex h-8 w-8 flex-none items-center justify-center rounded-lg bg-[#f3f3f8] text-[15px] dark:bg-white/[0.06]'>
              {source.icon}
            </span>
            <span className='min-w-0 flex-1'>
              <span
                className={clsxm(
                  'block truncate text-[13px] font-semibold',
                  tone.ink,
                )}
              >
                {source.name}
              </span>
              <span className={clsxm('block text-[11px]', tone.quiet)}>
                {source.meta}
              </span>
            </span>
            <span className='flex h-5 w-5 flex-none items-center justify-center rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-400/15 dark:text-emerald-300'>
              <CheckGlyph className='h-3 w-3' />
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function ComputerVisual() {
  const { t, lang } = useEmployeesT();
  const orders = new Intl.NumberFormat(
    lang === 'ar' ? 'ar-u-nu-latn' : lang,
  ).format(1240);
  return (
    <div className='w-full max-w-[290px] rounded-2xl bg-[#121219] p-3.5 font-mono text-[11.5px] leading-[1.7] text-white/80 shadow-[0_20px_40px_-24px_rgba(16,10,70,0.8)] ring-1 ring-white/10'>
      <p className='mb-2 flex gap-1.5'>
        <span className='h-2 w-2 rounded-full bg-white/20' />
        <span className='h-2 w-2 rounded-full bg-white/20' />
        <span className='h-2 w-2 rounded-full bg-white/20' />
      </p>
      <p dir='ltr' className='text-start'>
        <span className='text-primary-300'>$</span> python report.py orders.csv
      </p>
      <p dir='ltr' className='text-start text-white/50'>
        {t('Read {{count}} orders', { count: orders })}
      </p>
      <p
        dir='ltr'
        className='flex items-center gap-1.5 text-start text-emerald-300'
      >
        <CheckGlyph className='h-3 w-3' /> launch-week.png
      </p>
      <p className='mt-2.5 flex items-center gap-1.5 rounded-lg bg-white/[0.06] px-2.5 py-1.5 font-sans text-[11px] text-white/60'>
        <span className='h-1.5 w-1.5 animate-pulse rounded-full bg-primary-400' />
        {t('Searching the web')}
      </p>
    </div>
  );
}

function MediaVisual() {
  const { t } = useEmployeesT();
  return (
    <div className='grid w-full max-w-[290px] grid-cols-[1.4fr_1fr] gap-2'>
      <div className='relative aspect-[4/3] overflow-hidden rounded-xl ring-1 ring-black/5'>
        <Image
          src={BLOOM_BANNER}
          alt=''
          fill
          unoptimized
          sizes='170px'
          className='object-cover'
        />
        <span className='absolute bottom-1.5 start-1.5 rounded-md bg-white/90 px-1.5 py-0.5 text-[10px] font-semibold text-[#10101c]'>
          {t('Banner')}
        </span>
      </div>
      <div className='relative flex aspect-[3/4] items-center justify-center overflow-hidden rounded-xl bg-[linear-gradient(160deg,#f5d0dc,#c9b8f0)] ring-1 ring-black/5'>
        <span className='flex h-9 w-9 items-center justify-center rounded-full bg-white/90 text-[#10101c] shadow'>
          <svg
            viewBox='0 0 20 20'
            fill='currentColor'
            className='ms-0.5 h-3.5 w-3.5 rtl:-scale-x-100'
          >
            <path d='M6.3 2.84A1.5 1.5 0 004 4.11v11.78a1.5 1.5 0 002.3 1.27l9.34-5.89a1.5 1.5 0 000-2.54L6.3 2.84z' />
          </svg>
        </span>
        <span className='absolute bottom-1.5 start-1.5 rounded-md bg-white/90 px-1.5 py-0.5 text-[10px] font-semibold text-[#10101c]'>
          {t('Clip')} · <span dir='ltr'>0:08</span>
        </span>
      </div>
    </div>
  );
}

function ModelsVisual({
  models,
  modelCount,
}: {
  models: ToolkitModel[];
  modelCount: number;
}) {
  const { t } = useEmployeesT();
  return (
    <div className='w-full max-w-[290px] rounded-2xl bg-[#0d0c1f] p-2.5 text-white shadow-[0_20px_40px_-24px_rgba(16,10,70,0.8)] ring-1 ring-white/10'>
      <ul className='space-y-0.5'>
        {models.map(({ provider, title }, index) => {
          const brand = PROVIDER_LOGOS[provider];
          const ModelLogo = brand?.logo;
          return (
            <li
              key={title}
              className={clsxm(
                'flex items-center gap-2.5 rounded-xl px-2 py-1.5',
                index === 0 &&
                  'bg-white/[0.08] ring-1 ring-inset ring-white/10',
              )}
            >
              <span className='flex h-6 w-6 flex-none items-center justify-center rounded-md bg-white'>
                {ModelLogo ? (
                  <ModelLogo
                    className={clsxm('h-3.5 w-3.5', brand.className)}
                  />
                ) : null}
              </span>
              <span className='flex-1 truncate text-[12px] font-medium'>
                {title}
              </span>
              {index === 0 ? (
                <span className='rounded-full bg-primary-500/30 px-2 py-0.5 text-[10px] font-semibold text-primary-100'>
                  {t('High')}
                </span>
              ) : null}
            </li>
          );
        })}
      </ul>
      <p className='px-2 pb-0.5 pt-2 text-[11px] text-white/45'>
        {t('{{count}}+ models in total', { count: modelCount })}
      </p>
    </div>
  );
}

function SkillsVisual() {
  const { t } = useEmployeesT();
  const skills = [
    t('Research report'),
    t('Data analysis'),
    t('Documents'),
    t('Financial analysis'),
    t('Image generation'),
  ];
  return (
    <div className='flex w-full max-w-[290px] flex-wrap justify-center gap-1.5'>
      {skills.map((skill, index) => (
        <span
          key={skill}
          className={clsxm(
            panel,
            'flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold',
            tone.ink,
          )}
          style={{ transform: `rotate(${[-2, 1.5, -1, 2, -1.5][index]}deg)` }}
        >
          <span className='h-1.5 w-1.5 rounded-full bg-primary-500' />
          {skill}
        </span>
      ))}
      <span className='rounded-full border border-dashed border-[#0f0f28]/25 px-3 py-1.5 text-xs font-semibold text-[#56566d] dark:border-white/20 dark:text-[#a4a4bd]'>
        + {t('Your own')}
      </span>
    </div>
  );
}
